// Base Cart Item Component
class CartItem extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  static get observedAttributes() {
    return ['data-key', 'data-variant-id', 'data-quantity'];
  }

  connectedCallback() {
    if (!this.initialized) {
      this.bindEvents();
      this.initialized = true;
    }
  }

  bindEvents() {
    // Use event delegation for better performance
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('quantity:changed', this.handleQuantityChange.bind(this));
  }

  handleClick(e) {
    if (e.target.classList.contains('item-remove') || e.target.closest('.item-remove')) {
      e.preventDefault();
      this.dispatchEvent(new CustomEvent('cart-item:remove', {
        bubbles: true,
        detail: { key: this.dataset.key }
      }));
    }
  }

  handleQuantityChange(e) {
    this.dispatchEvent(new CustomEvent('cart-item:quantity-changed', {
      bubbles: true,
      detail: {
        key: this.dataset.key,
        quantity: e.detail.quantity
      }
    }));
  }
}

// Quantity Controls Component
class QuantityControls extends HTMLElement {
  constructor() {
    super();
    this.quantity = 1;
    this.minQuantity = 1;
    this.maxQuantity = 999;
    this.key = '';
    this.initialized = false;
  }

  static get observedAttributes() {
    return ['data-quantity', 'data-min-quantity', 'data-max-quantity', 'data-key'];
  }

  connectedCallback() {
    if (!this.initialized) {
      this.updateFromAttributes();
      this.bindEvents();
      this.initialized = true;
    }
  }

  updateFromAttributes() {
    this.quantity = parseInt(this.dataset.quantity) || 1;
    this.minQuantity = parseInt(this.dataset.minQuantity) || 1;
    this.maxQuantity = parseInt(this.dataset.maxQuantity) || 999;
    this.key = this.dataset.key || '';
  }

  bindEvents() {
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', this.handleChange.bind(this));
  }

  handleClick(e) {
    if (e.target.classList.contains('quantity-btn') || e.target.closest('.quantity-btn')) {
      e.preventDefault();
      let action = e.target.dataset.action;
      if(e.target.closest('.quantity-btn')){
        action = e.target.closest('.quantity-btn').dataset.action;
      }

      if (action === 'increase' && this.quantity < this.maxQuantity) {
        this.updateQuantity(this.quantity + 1);
      } else if (action === 'decrease' && this.quantity > this.minQuantity) {
        this.updateQuantity(this.quantity - 1);
      }
    }
  }

  handleChange(e) {
    if (e.target.hasAttribute('data-quantity-input')) {
      const newQuantity = Math.max(
        this.minQuantity, 
        Math.min(this.maxQuantity, parseInt(e.target.value) || this.minQuantity)
      );
      this.updateQuantity(newQuantity);
    }
  }

  updateQuantity(newQuantity) {
    if (newQuantity !== this.quantity) {
      this.quantity = newQuantity;
      this.dataset.quantity = newQuantity;

      this.dispatchEvent(new CustomEvent('quantity:changed', {
        bubbles: true,
        detail: {
          quantity: newQuantity,
          key: this.key
        }
      }));
    }
  }
}

// Shipping Progress Component
class ShippingProgress extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (!this.initialized) {
      this.bindEvents();
      this.initialized = true;
    }
  }

  bindEvents() {
    // Listen for progress updates from the main cart
    this.addEventListener('shipping:progress-updated', () => {
      // This event can be used for additional effects if needed
    });
  }
}

// Gift Progress Component
class GiftProgress extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (!this.initialized) {
      this.bindEvents();
      this.initialized = true;
    }
  }

  bindEvents() {
    this.addEventListener('gift:progress-updated', () => {
      const isFinished = this.querySelector('.gift-progress-text')?.dataset.progressFinished === 'true';
      if (isFinished) {
        console.log('Free gift unlocked!');
      }
    });
  }
}

// Product Recommendations Component  
class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this._isLoaded = false;
  }

  connectedCallback() {
    if (!this.initialized) {
      this.bindEvents();
      this.initialized = true;
    }
    this.loadRecommendations();
  }

  bindEvents() {
    this.addEventListener('click', (e) => {
      if (e.target.classList.contains('recommendation-add-btn')) {
        e.preventDefault();
        const variantId = e.target.dataset.variantId;

        if (variantId) {
          this.dispatchEvent(new CustomEvent('recommendation:add-to-cart', {
            bubbles: true,
            detail: { variantId, quantity: 1 }
          }));
        }
      }
    });
  }

  async loadRecommendations() {
    if (this._isLoaded) return;
    this._isLoaded = true;

    try {
      const sectionEl = this.closest('.shopify-section');
      const sectionId = this.extractSectionId(sectionEl);
      const intent = this.getAttribute('intent') || 'related';
      const productId = this.getAttribute('product');
      const limit = this.getAttribute('limit') || 4;

      if (!productId || !sectionId) {
        this.remove();
        return;
      }

      const url = this.buildRecommendationsUrl(productId, limit, sectionId, intent);
      const response = await fetch(url, { 
        headers: { 'Accept': 'text/html' },
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const text = await response.text();
      const recommendations = this.parseRecommendations(text);

      if (recommendations && recommendations.childElementCount > 0) {
        this.replaceChildren(...recommendations.childNodes);
        this.hidden = false;
      } else {
        this.remove();
      }
    } catch (err) {
      console.error('Failed to load product recommendations:', err);
      this.remove();
    }
  }

  extractSectionId(sectionNode) {
    if (!sectionNode) return '';
    if (sectionNode.id && sectionNode.id.startsWith('shopify-section-')) {
      return sectionNode.id.replace('shopify-section-', '');
    }
    return sectionNode.getAttribute('data-section-id') || '';
  }

  buildRecommendationsUrl(productId, limit, sectionId, intent) {
    const params = new URLSearchParams({
      product_id: productId,
      limit: limit,
      section_id: sectionId,
      intent: intent
    });
    return `${Shopify.routes.root}recommendations/products?${params.toString()}`;
  }

  parseRecommendations(htmlText) {
    const html = document.createElement('div');
    html.innerHTML = htmlText;
    return html.querySelector('product-recommendations');
  }
}

class ChooseGift extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }
  
  connectedCallback() {
    if (this.initialized) return;
    this.cartComponent = this.closest('custom-cart') || window.customCart?.drawer || null;
    this.bindEvents();
    this.updateVisibility();
    this.initialized = true;
  }
  
  bindEvents() {
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('.choose-gift-product-add-btn');
      if (!btn) return;
      e.preventDefault();
      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId || this.isAdding) return;
      this.isAdding = true;
      this.toggleButtonsDisabled(true);
      window.customCart
        .addProduct(variantId, 1, { _choose_gift_product: true })
        .then(() => {
          // After cart refresh the section will likely re-render without this block
          // Hide defensively in case HTML isn't re-rendered yet
          this.hide();
        })
        .catch(() => {
          // noop, error is handled upstream
        })
        .finally(() => {
          this.isAdding = false;
          this.toggleButtonsDisabled(false);
        });
    });
    
    // React to cart updates
    const target = this.cartComponent || document;
    this._eventTarget = target;
    this._onCartUpdated = () => this.updateVisibility();
    this._onItemAdded = () => this.updateVisibility();
    target.addEventListener('cart:updated', this._onCartUpdated);
    target.addEventListener('cart:item-added', this._onItemAdded);
  }

  disconnectedCallback() {
    if (this._eventTarget) {
      if (this._onCartUpdated) this._eventTarget.removeEventListener('cart:updated', this._onCartUpdated);
      if (this._onItemAdded) this._eventTarget.removeEventListener('cart:item-added', this._onItemAdded);
    }
  }
  
  toggleButtonsDisabled(disabled) {
    this.querySelectorAll('.choose-gift-product-add-btn').forEach(btn => {
      btn.disabled = !!disabled;
    });
  }
  
  getConfig() {
    const cart = this.cartComponent;
    if (!cart) return { enable: false, order_amount: Number.MAX_SAFE_INTEGER };
    // Prefer parsed config on cart component
    if (cart.chooseGiftProductData) return cart.chooseGiftProductData;
    // Fallback: parse from embedded script JSON within cart section if available
    const scriptData = cart.querySelector('script[type="application/json"]');
    try {
      if (scriptData) {
        const data = JSON.parse(scriptData.textContent);
        return data.choose_gift_product_data || { enable: false, order_amount: Number.MAX_SAFE_INTEGER };
      }
    } catch (e) {
      console.error('ChooseGift: failed to parse config', e);
    }
    return { enable: false, order_amount: Number.MAX_SAFE_INTEGER };
  }
  
  getCartTotalsCents() {
    const cart = this.cartComponent;
    if (!cart) return 0;
    // Prefer cart JSON total in cents if available
    if (cart.cart && typeof cart.cart.total_price === 'number') return cart.cart.total_price;
    // Fallback from attribute stored in cents
    const attr = cart.getAttribute('data-current-total');
    if (attr) return Number(attr) || 0;
    // Last resort from currentTotal dollars → cents
    if (typeof cart.currentTotal === 'number') return Math.round(cart.currentTotal * 100);
    return 0;
  }
  
  isGiftAlreadyInCart() {
    const cart = this.cartComponent;
    if (!cart || !cart.cart || !Array.isArray(cart.cart.items)) return false;
    return cart.cart.items.some(item => {
      const prop = item && item.properties && item.properties._choose_gift_product;
      return prop === true || prop === 'true';
    });
  }
  
  shouldDisplay() {
    const cfg = this.getConfig();
    if (!cfg || !cfg.enable) return false;
    if (this.isGiftAlreadyInCart()) return false;
    const totalCents = this.getCartTotalsCents();
    const thresholdCents = Number(cfg.order_amount) || 0;
    return totalCents >= thresholdCents;
  }
  
  updateVisibility() {
    if (this.shouldDisplay()) {
      this.show();
      this.toggleButtonsDisabled(false);
    } else {
      this.hide();
    }
  }
  
  show() {
    this.hidden = false;
    this.style.display = '';
  }
  
  hide() {
    this.hidden = true;
    this.style.display = 'none';
  }
}

// Selling Plan Selector Component
class SellingPlanSelector extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.isUpdating = false;
  }

  static get observedAttributes() {
    return ['data-key', 'data-variant-id', 'data-current-plan-id'];
  }

  connectedCallback() {
    if (!this.initialized) {
      this.bindEvents();
      this.initialized = true;
    }
  }

  bindEvents() {
    const select = this.querySelector('.selling-plan-select');
    if (select) {
      select.addEventListener('change', this.handlePlanChange.bind(this));
    }
  }

  async handlePlanChange(e) {
    if (this.isUpdating) return;
    
    const newPlanId = e.target.value;
    const currentPlanId = this.dataset.currentPlanId;
    
    if (newPlanId === currentPlanId) return;

    this.isUpdating = true;
    this.setLoadingState(true);
    
    try {
      await this.updateSellingPlan(newPlanId);
      this.dataset.currentPlanId = newPlanId;
      
      // Remove loading immediately after cart refresh
      this.setLoadingState(false);
      
      // Small delay for success message
      await new Promise(resolve => setTimeout(resolve, 200));
      
      this.showSuccessMessage();
    } catch (error) {
      console.error('Error updating selling plan:', error);
      this.showErrorMessage(error.message);
      // Revert selection
      e.target.value = currentPlanId;
      // Make sure to remove loading on error
      this.setLoadingState(false);
    } finally {
      this.isUpdating = false;
      // Ensure loading is always removed
      this.setLoadingState(false);
    }
  }

  async updateSellingPlan(newPlanId) {
    const key = this.dataset.key;
    const variantId = this.dataset.variantId;
    
    // Get cart component for accessing cart data
    const cartComponent = this.closest('custom-cart');
    if (!cartComponent) {
      throw new Error('Cart component not found');
    }

    // Get current item data
    const currentItem = cartComponent.cart?.items?.find(item => item.key === key);
    if (!currentItem) {
      throw new Error('Current item not found in cart');
    }

    const quantity = currentItem.quantity;
    const properties = currentItem.properties || {};


    try {
      // Always replace the item - remove old, add new with plan
      await this.replaceItemWithNewPlan(key, variantId, quantity, newPlanId, properties);

      // Refresh cart after successful update
      await cartComponent.refreshCart();
      
    } catch (error) {
      console.error('Failed to update selling plan:', error);
      throw error;
    }
  }

  async replaceItemWithNewPlan(key, variantId, quantity, planId, properties) {
    // Step 1: Remove current item from cart
    const removeResponse = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: key,
        quantity: 0
      })
    });

    if (!removeResponse.ok) {
      throw new Error('Failed to remove item from cart');
    }

    // Step 2: Prepare payload for adding item with new plan
    const addPayload = {
      id: variantId,
      quantity: quantity,
      properties: properties
    };

    // Only add selling_plan if it's not one-time purchase
    if (planId !== 'one-time') {
      addPayload.selling_plan = planId;
    }

    // Step 3: Add item back with new selling plan
    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(addPayload)
    });

    if (!addResponse.ok) {
      const errorData = await addResponse.json();
      
      // Try to restore original item (without selling plan as fallback)
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity,
            properties: properties
          })
        });
      } catch (revertError) {
        console.error('Failed to restore original item:', revertError);
      }
      
      // Provide specific error message based on the error
      let errorMessage = 'Failed to add item with selected plan';
      
      if (errorData.description) {
        if (errorData.description.toLowerCase().includes('selling plan') || 
            errorData.description.toLowerCase().includes('subscription')) {
          errorMessage = 'This variant doesn\'t support the selected subscription plan';
        } else {
          errorMessage = errorData.description;
        }
      }
      
      throw new Error(errorMessage);
    }

    return await addResponse.json();
  }

  setLoadingState(loading) {
    const select = this.querySelector('.selling-plan-select');
    if (select) {
      select.disabled = loading;
      if (loading) {
        select.style.opacity = '0.6';
        select.style.pointerEvents = 'none';
      } else {
        select.style.opacity = '';
        select.style.pointerEvents = '';
      }
    }
    
    // Try multiple ways to find the cart component
    let cartComponent = this.closest('custom-cart');
    
    if (!cartComponent) {
      cartComponent = document.getElementById('customCart');
    }
    
    if (!cartComponent) {
      cartComponent = document.querySelector('.custom-cart');
    }
    
    if (cartComponent && typeof cartComponent.setLoading === 'function') {
      cartComponent.setLoading(loading);
    }
  }

  showSuccessMessage() {
    const cartComponent = this.closest('custom-cart');
    if (cartComponent && cartComponent.showDiscountMessage) {
      cartComponent.showDiscountMessage(
        window.CART_TRANSLATIONS?.planUpdateSuccess || 'Plan updated successfully!',
        'success'
      );
    }
  }

  showErrorMessage(customMessage = null) {
    const cartComponent = this.closest('custom-cart');
    if (cartComponent && cartComponent.showDiscountMessage) {
      const message = customMessage || window.CART_TRANSLATIONS?.planUpdateError || 'Failed to update plan';
      cartComponent.showDiscountMessage(message, 'error');
    }
  }
}

class CustomCart extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.isLoading = false;
    this.isAutoGiftMutation = false;
    this.suppressedGiftVariantIds = new Set();
    this.activeDiscounts = (this.dataset.itemsDiscounts || '').split(',').filter(Boolean);
    this.initializeProperties();
    this.init();
  }

  initializeProperties() {
    try {
      const shippingProgress = this.querySelector('shipping-progress');
      const scriptData = this.querySelector('script[type="application/json"]');
      
      if (shippingProgress) {
        this.freeShippingThreshold = shippingProgress.dataset.threshold;
        this.currencyCode = shippingProgress.dataset.currencyCode;
        this.showShippingProgress = shippingProgress.dataset.showShippingProgress;
      }
      
      if (this.querySelector('product-recommendations')) {
        this.showRecommendations = this.querySelector('product-recommendations').dataset.showRecommendations;
      }
      
      if (scriptData) {
        const data = JSON.parse(scriptData.textContent);
        this.promos = data.data || [];
        this.cart = data.cart || {};
        this.giftProductData = data.gift_product_data || {};
        this.chooseGiftProductData = data.choose_gift_product_data || {};
      }
      
      this.currentTotal = (this.dataset.currentTotal || 0) / 100.0;
      this.productsIds = (this.dataset.productsIds || '').split(',').filter(Boolean).map(id => Number(id));
    } catch (error) {
      console.error('Error initializing cart properties:', error);
      this.promos = [];
      this.cart = {};
      this.productsIds = [];
    }
  }

  init() {
    this.overlay = document.getElementById('cartOverlay');
    this.closeBtn = this.querySelector('.cart-close');
    this.checkoutBtn = this.querySelector('#checkoutBtn');
    this.discountForm = this.querySelector('#discountForm');
    this.discountInput = this.querySelector('#discountInput');
    this.discountSubmitBtn = this.querySelector('#discountSubmitBtn');
    this.discountMessage = this.querySelector('#discountMessage');
    
    this.bindEvents();
    this.refreshCart();
    
    // Mark body as having custom cart
    document.body.classList.add('custom-cart-active');
  }

  bindEvents() {
    // Close cart events
    this.closeBtn.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', () => this.close());
    
    // Keyboard events
    document.addEventListener('keydown', this.handleKeydown.bind(this));
    
    // Cart item events
    this.addEventListener('cart-item:remove', this.handleCartItemRemove.bind(this));
    this.addEventListener('cart-item:quantity-changed', this.handleCartItemQuantityChanged.bind(this));
    
    // Recommendation events
    this.addEventListener('recommendation:add-to-cart', this.handleRecommendationAdd.bind(this));
    this.addEventListener('click', this.handleRecommendationClick.bind(this));

    // Shipping progress events
    this.addEventListener('shipping:unlocked', this.handleShippingUnlocked.bind(this));
    
    // Discount form
    if (this.discountForm) {
      this.discountForm.addEventListener('submit', this.handleDiscountSubmit.bind(this));
    }
    
    // Checkout button
    this.checkoutBtn.addEventListener('click', this.handleCheckout.bind(this));
    
    // Listen for cart updates from other parts of the site
    document.addEventListener('cart:updated', this.handleCartUpdate.bind(this));
    document.addEventListener('cart:refresh', () => this.refreshCart());
    
    // Discount removal events
    this.addEventListener('click', this.handleDiscountRemoval.bind(this));
  }

  handleKeydown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  handleCartItemRemove(e) {
    const { key } = e.detail || {};
    if (!key) {
      return;
    }

    if (!this.isAutoGiftMutation && this.cart && Array.isArray(this.cart.items)) {
      const removedItem = this.cart.items.find(item => item.key === key);
      if (removedItem) {
        const removedVariantId = Number(removedItem.id);
        const giftVariantId = Number(this.giftProductData?.id);

        if (removedVariantId && giftVariantId && removedVariantId === giftVariantId) {
          this.suppressedGiftVariantIds.add(giftVariantId);
        }
      }
    }

    this.removeItem(key);
  }

  handleCartItemQuantityChanged(e) {
    this.updateQuantity(e.detail.key, e.detail.quantity);
  }

  handleRecommendationAdd(e) {
    this.addProduct(e.detail.variantId, e.detail.quantity).then(() => {
      // After adding from recommendations, force a refresh to update list
      this.refreshCart();
    });
  }

  handleRecommendationClick(e) {
    const btn = e.target.closest('.recommendation-add-btn');
    if (btn) {
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (variantId) {
        this.addProduct(variantId, 1);
      }
    }
  }

  handleShippingUnlocked() {
    this.showSuccessMessage(window.CART_TRANSLATIONS.successUnlockedShipping);
  }

  async handleDiscountSubmit(event) {
    event.preventDefault();
    if (!this.discountInput) return;
    
    const discountCode = this.discountInput.value.trim();
    
    if (!discountCode) {
      this.showDiscountMessage(window.CART_TRANSLATIONS.discountEnterCode, 'error');
      return;
    }

    const promos = Array.isArray(this.promos) ? this.promos : [];
    for (const promo of promos) {
      const codes = Array.isArray(promo.discount_codes) ? promo.discount_codes : [];
      if (codes.includes(discountCode)) {
        await this.applyDiscountPromo(promo);
       
      }
    }

    await this.applyDiscountCode(discountCode);
  }

  handleDiscountRemoval(event) {
    // Check if clicked element is a discount item or its close icon
    const discountItem = event.target.closest('.discount-item');
    if (!discountItem) return;
    
    // Check if clicked on close icon or the discount item itself
    const isCloseIcon = event.target.closest('.icon') || event.target.classList.contains('icon');
    if (isCloseIcon || event.target === discountItem) {
      event.preventDefault();
      this.removeDiscountCode(discountItem);
    }
  }

  async removeDiscountCode(discountItem) {
    try {
      // Get discount code from the discount item
      const discountText = discountItem.querySelector('span')?.textContent?.trim();
      if (!discountText) return;
      
      // Remove from active discounts array
      const discountIndex = this.activeDiscounts.indexOf(discountText);
      if (discountIndex > -1) {
        this.activeDiscounts.splice(discountIndex, 1);
      }
      
      // Update cart with new discount codes
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          discount: this.activeDiscounts.join(',')
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove discount code');
      }

      // Refresh cart to update UI
      await this.refreshCart();
      
      // Show success message
      this.showDiscountMessage(window.CART_TRANSLATIONS.discountRemoved, 'success');
      
    } catch (error) {
      console.error('Error removing discount code:', error);
      this.showDiscountMessage(window.CART_TRANSLATIONS.discountRemoveFailed, 'error');
      
      // Restore the discount code if removal failed
      if (discountText && !this.activeDiscounts.includes(discountText)) {
        this.activeDiscounts.push(discountText);
      }
    }
  }

  handleCheckout() {
    if (!this.isLoading && !this.checkoutBtn.disabled) {
      this.checkoutBtn.style.pointerEvents = 'none';
      this.checkoutBtn.innerHTML = CART_TRANSLATIONS.checkoutRedirecting;
      window.location.href = '/checkout';
    }
  }

  handleCartUpdate(event) {
    if (event.detail) {
      this.refreshCart();
    }
  }

  // Handle gift products update when cart changes
  // handleGiftProductsUpdate() {
  //   if (this.giftProductData) {
  //     this.initializeProperties();
  //     this.updateGiftProduct(this.giftProductData);
  //   }
  // }

  open() {
    this.isOpen = true;
    this.classList.add('is-open');
    this.overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    
    this.closeBtn.focus();
    this.refreshCart();
    
    this.dispatchEvent(new CustomEvent('cart:opened', { bubbles: true }));

    const carousel = this.querySelector('announcement-bar-carousel');
    if (carousel && carousel.player) {
        carousel.player.start();
    }
  }

  close() {
    this.isOpen = false;
    this.classList.remove('is-open');
    this.overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
    
    if (this.discountMessage) {
      this.hideDiscountMessage();
    }
    
    this.dispatchEvent(new CustomEvent('cart:closed', { bubbles: true }));
  }

  checkGiftInCart(id, props = null){
    if (!this.cart || !Array.isArray(this.cart.items)) {
      return false;
    }

    const giftId = Number(id);

    if (props) {
      return this.cart.items.some(item => Number(item.id) === giftId && item.properties && item.properties[props] === true);
    }

    return this.cart.items.some(item => Number(item.id) === giftId && this.isGiftLineItem(item));
  }

  isGiftLineItem(item) {
    const properties = item && item.properties;
    if (!properties) return false;
    return (
      properties._gift_product === true ||
      properties._gift_product === 'true' ||
      properties.gift_product === true ||
      properties.gift_product === 'true'
    );
  }

  checkThisProductInCart(id){
    if (!this.cart || !Array.isArray(this.cart.items)) {
      return false;
    }

    const productId = Number(id);
    return this.cart.items.some(item => Number(item.id) === productId);
  }

  updateGiftProduct(data){
    if (!data || !data.id || !this.cart || !Array.isArray(this.cart.items)) {
      return;
    }

    const giftVariantId = Number(data.id);
    const staleGift = this.cart.items.find(
      item => this.isGiftLineItem(item) && Number(item.id) !== giftVariantId
    );

    if (staleGift && !this.isLoading) {
      this.isAutoGiftMutation = true;
      const removalPromise = this.removeItem(staleGift.key);
      if (removalPromise && typeof removalPromise.finally === 'function') {
        removalPromise.finally(() => {
          this.isAutoGiftMutation = false;
        });
      } else {
        this.isAutoGiftMutation = false;
      }
      return;
    }

    const giftItem = this.cart.items.find(item => Number(item.id) === giftVariantId && this.isGiftLineItem(item));
    const giftInCart = Boolean(giftItem);

    const giftLinePriceCents = giftItem ? Number(
      giftItem.final_line_price ??
      giftItem.original_line_price ??
      (giftItem.price != null && giftItem.quantity != null ? giftItem.price * giftItem.quantity : 0)
    ) : 0;

    const giftLinePrice = giftLinePriceCents / 100;
    const currentTotal = Number(this.currentTotal || 0);
    const subtotalWithoutGift = currentTotal - giftLinePrice;
    const meetsThreshold = Number(data.order_amount) <= subtotalWithoutGift;
    const isSuppressed = this.suppressedGiftVariantIds.has(giftVariantId);

    if (!meetsThreshold) {
      this.suppressedGiftVariantIds.delete(giftVariantId);
    }

    if (data.enable && meetsThreshold) {
      if (isSuppressed) {
        return;
      }

      if (!giftInCart) {
        console.log("adding gift product", giftInCart);
        if (!this.isLoading) {
          this.addProduct(data.id, 1, {
            "_gift_product": true
          });
        } else {
          console.log('Cart is loading or not ready, skipping gift product addition');
        }
      }
    } else if (giftInCart && !this.isLoading) {
      this.isAutoGiftMutation = true;
      const removalPromise = this.removeItem(giftItem.key);
      if (removalPromise && typeof removalPromise.finally === 'function') {
        removalPromise.finally(() => {
          this.isAutoGiftMutation = false;
          this.suppressedGiftVariantIds.delete(giftVariantId);
        });
      } else {
        this.isAutoGiftMutation = false;
        this.suppressedGiftVariantIds.delete(giftVariantId);
      }
    } else if (!giftInCart) {
      // If the gift isn't present and we're below threshold (or disabled), ensure suppression is cleared.
      this.suppressedGiftVariantIds.delete(giftVariantId);
    }
  }

  updateGiftProgress(newCartSection) {
    const newGiftProgress = newCartSection.querySelector('gift-progress');
    const currentGiftProgress = this.querySelector('gift-progress');
    
    if (newGiftProgress && currentGiftProgress) {
        currentGiftProgress.innerHTML = newGiftProgress.innerHTML;
        const progressEvent = new CustomEvent('gift:progress-updated', { bubbles: true });
        currentGiftProgress.dispatchEvent(progressEvent);
    } else if (newGiftProgress && !currentGiftProgress) {
        const header = this.querySelector('.cart-header');
        if (header) header.insertAdjacentElement('afterend', document.importNode(newGiftProgress, true));
    }
  }

  async addProduct(variantId, quantity = 1, properties = {}, sellingPlan) {
    if (this.isLoading) return;
    
    this.setLoading(true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity,
            properties: properties,
            selling_plan: sellingPlan
          }]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || CART_TRANSLATIONS.failedAddProduct);
      }

      const result = await response.json();
      
      await this.refreshCart();
      
      this.dispatchEvent(new CustomEvent('cart:item-added', { 
        bubbles: true, 
        detail: { items: result.items || [result] }
      }));

      return result;
    } catch (error) {
      console.error('Error adding product:', error);
      this.showError(window.CART_TRANSLATIONS.failedAddProduct + ': ' + error.message);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async updateQuantity(key, quantity) {
    if (this.isLoading) return;
    
    this.setLoading(true);
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || window.CART_TRANSLATIONS.failedUpdateQuantity);
      }

      await this.refreshCart();
      
      // Trigger update event
      this.dispatchEvent(new CustomEvent('cart:updated', { 
        bubbles: true, 
        detail: { key, quantity }
      }));
      
    } catch (error) {
      console.error('Error updating quantity:', error);
      this.showError(window.CART_TRANSLATIONS.failedUpdateQuantity + ': ' + error.message);
      await this.refreshCart();
    } finally {
      this.setLoading(false);
    }
  }

  async removeItem(key) {
    await this.updateQuantity(key, 0);
  }

  async applyDiscountCode(code) {
    if (this.isLoading || !this.discountSubmitBtn) return;
   
    this.discountSubmitBtn.disabled = true;
    this.discountSubmitBtn.textContent = window.CART_TRANSLATIONS.discountApplying;
    if(!this.activeDiscounts.includes(code)) {
      this.setLoading(true);
      this.activeDiscounts.push(code);

      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            discount: this.activeDiscounts.toString()
          })
        });
  
        if (!response.ok) {
          throw new Error(window.CART_TRANSLATIONS.discountError);
        }
  
        await this.refreshCart();

        this.showDiscountMessage(window.CART_TRANSLATIONS.discountSuccess, 'success');
        this.discountInput.value = '';
        
      } catch (error) {
        console.error('Error applying discount:', error);
        this.showDiscountMessage(window.CART_TRANSLATIONS.discountError, 'error');
      } finally {
        this.setLoading(false);
        this.discountSubmitBtn.disabled = false;
        this.discountSubmitBtn.textContent = window.CART_TRANSLATIONS.discountApplyText;
        await window.setCartAttribute('active_codes', this.activeDiscounts.toString());
        await this.refreshCart()
      }
    }
  }

  async applyDiscountPromo(promo) {
    if (this.isLoading || !this.discountSubmitBtn) return;

    if(Number(promo.minimum_order_amount) < Number(this.currentTotal) && !this.checkGiftInCart(promo.gift_product.id)) {
      switch(promo.type) {
        case 'simple_discout_gift':
          await this.addProduct(promo.gift_product.id, 1);
          await this.refreshCart();
          break;
        case 'discout_gift_based_on_product':
          if(promo.necessary_products.split(',').every(id => this.productsIds.includes(Number(id)))) {
            await this.addProduct(promo.gift_product.id, 1);
            await this.refreshCart();
          }
        break;
      }
    }
  }

  async refreshCart() {
    try {
      //const response = await fetch(`${window.location.pathname}?section_id=custom-cart`);
      const response = await fetch(`/`);
      
      if (!response.ok) {
        throw new Error(`Failed to refresh cart section: ${response.status}`);
      }

      const html = await response.text();
      const newCartSection = this.parseCartSection(html);
      if (newCartSection) {
        this.updateCartData(newCartSection);
        this.updateCartElements(newCartSection);
        this.initializeComponents();
        this.updateGiftProduct(this.giftProductData);
      }

      return { item_count: this.getCartItemCount() };
    } catch (error) {
      console.error('Error refreshing cart:', error);
      return this.fallbackRefreshCart();
    }
  }

  parseCartSection(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.querySelector('.custom-cart');
  }

  updateCartData(newCartSection) {
    const newCartTotal = newCartSection.getAttribute('data-current-total');
    const newCartProductsIds = newCartSection.getAttribute('data-products-ids');
    const newCartScript = newCartSection.querySelector('script[type="application/json"]');
    const newCartItemsDiscounts = newCartSection.getAttribute('data-items-discounts');
    
    if (newCartTotal) {
      this.setAttribute('data-current-total', newCartTotal);
      this.currentTotal = (newCartTotal || 0) / 100.0;
    }
    
    if (newCartProductsIds) {
      this.setAttribute('data-products-ids', newCartProductsIds);
      this.productsIds = newCartProductsIds.split(',').filter(Boolean).map(id => Number(id));
    }
    
    if (newCartScript) {
      try {
        const newCart = JSON.parse(newCartScript.textContent);
        this.cart = newCart.cart || this.cart;
        if (newCart.gift_product_data) {
          this.giftProductData = newCart.gift_product_data;
        }
        if (newCart.choose_gift_product_data) {
          this.chooseGiftProductData = newCart.choose_gift_product_data;
        }

      } catch (error) {
        console.error('Error parsing cart data:', error);
      }
    }

    if (newCartItemsDiscounts) {
      this.setAttribute('data-items-discounts', newCartItemsDiscounts);
      this.activeDiscounts = newCartItemsDiscounts.split(',');
    }
  }

  updateCartElements(newCartSection) {
    // Update cart items
    this.updateElement('#cartItems', newCartSection);
    
    // Update cart summary
    this.updateElement('.cart-summary', newCartSection);

    // Update item count in header
    this.updateElement('[data-cart-count]', newCartSection);
    
    // Update checkout button
    this.updateCheckoutButton(newCartSection);
    
    // Update shipping progress
    this.updateShippingProgress(newCartSection);

    // Update gift progress
    this.updateGiftProgress(newCartSection);
    
    // Update product recommendations
    this.updateRecommendations(newCartSection);
  }

  updateElement(selector, newCartSection) {
    const newElement = newCartSection.querySelector(selector);
    const currentElement = this.querySelector(selector);
    if (newElement && currentElement) {
      currentElement.innerHTML = newElement.innerHTML;
    }
  }

  updateCheckoutButton(newCartSection) {
    const newCheckoutBtn = newCartSection.querySelector('#checkoutBtn');
    const currentCheckoutBtn = this.querySelector('#checkoutBtn');
    if (newCheckoutBtn && currentCheckoutBtn) {
      currentCheckoutBtn.disabled = newCheckoutBtn.disabled;
      currentCheckoutBtn.textContent = newCheckoutBtn.textContent;
    }
  }

  updateShippingProgress(newCartSection) {
    const newShippingProgress = newCartSection.querySelector('shipping-progress');
    const currentShippingProgress = this.querySelector('shipping-progress');
    if (newShippingProgress && currentShippingProgress) {
      currentShippingProgress.innerHTML = newShippingProgress.innerHTML;
      const progressEvent = new CustomEvent('shipping:progress-updated', { bubbles: true });
      currentShippingProgress.dispatchEvent(progressEvent);
    }
  }

  updateRecommendations(newCartSection) {
    const newRecommendations = newCartSection.querySelector('product-recommendations');
    const currentRecommendations = this.querySelector('product-recommendations');
    
    if (currentRecommendations) {
      if (newRecommendations) {
        const importedRecommendations = document.importNode(newRecommendations, true);
        currentRecommendations.replaceWith(importedRecommendations);
      } else {
        currentRecommendations.remove();
      }
    } else if (newRecommendations) {
      const container = this.querySelector('.custom-cart-inner') || this;
      container.appendChild(document.importNode(newRecommendations, true));
    }
  }


  async fallbackRefreshCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart data
      this.cart = cart;
      this.currentTotal = (cart.total_price || 0) / 100.0;
      this.productsIds = cart.items.map(item => item.product_id);
      
      return cart;
    } catch (error) {
      console.error('Error with fallback refresh:', error);
      return null;
    }
  }

  getCartItemCount() {
    const cartItems = this.querySelectorAll('cart-item:not([style*="display: none"])');
    return cartItems.length;
  }

  initializeComponents() {
    // Re-initialize quantity controls
    const quantityControls = this.querySelectorAll('quantity-controls');
    quantityControls.forEach(control => {
      if (control.isConnected && !control.initialized) {
        control.connectedCallback();
      }
    });
    
    // Re-initialize cart items
    const cartItems = this.querySelectorAll('cart-item');
    cartItems.forEach(item => {
      if (item.isConnected && !item.initialized) {
        item.connectedCallback();
      }
    });

    // Re-initialize selling plan selectors
    const sellingPlanSelectors = this.querySelectorAll('selling-plan-selector');
    sellingPlanSelectors.forEach(selector => {
      if (selector.isConnected && !selector.initialized) {
        selector.connectedCallback();
      }
    });

    // Re-initialize choose gift block if present
    const chooseGiftBlocks = this.querySelectorAll('choose-gift');
    chooseGiftBlocks.forEach(block => {
      if (block.isConnected && !block.initialized && typeof block.connectedCallback === 'function') {
        block.connectedCallback();
      }
      if (typeof block.updateVisibility === 'function') {
        block.updateVisibility();
      }
    });

    // Re-initialize announcement carousel
    this.querySelectorAll('announcement-bar-carousel')?.forEach(carousel => {
      if (carousel.connectedCallback) {
        carousel.connectedCallback(); 
      }
      if (this.isOpen && carousel.player) {
        carousel.player.start();
      }
    });
  }

  setLoading(loading) {
    this.isLoading = loading;
    this.classList.toggle('loading', loading);
  }

  showDiscountMessage(message, type) {
    if (!this.discountMessage) return;
    
    this.discountMessage.textContent = message;
    this.discountMessage.className = `discount-message ${type} show`;
    
    setTimeout(() => {
      this.hideDiscountMessage();
    }, 4000);
  }

  hideDiscountMessage() {
    if (!this.discountMessage) return;
    
    this.discountMessage.classList.remove('show');
    setTimeout(() => {
      this.discountMessage.textContent = '';
      this.discountMessage.className = 'discount-message';
    }, 300);
  }

  showSuccessMessage(message) {
    // Simple success notification
    console.log(message);
  }

  showError(message) {
    console.error(message);
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('{{ cart.currency.iso_code | downcase }}', {
      style: 'currency',
      currency: this.currencyCode,
      minimumFractionDigits: 2
    }).format(cents / 100);
  }
}

class AnnouncementBarCarousel extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.timer = null;
  }

  connectedCallback() {
    this.initialized = true;
    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.stop();
    const slides = this.querySelectorAll('.announcement-slide');
    const seconds = parseInt(this.getAttribute('autoplay'), 10) || 5;
    if (slides.length < 2) return;

    let index = Math.max(0, Array.from(slides).findIndex(slide => slide.classList.contains('is-selected')));
    this.timer = setInterval(() => {
      slides[index].classList.remove('is-selected');
      index = (index + 1) % slides.length;
      slides[index].classList.add('is-selected');
    }, seconds * 1000);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }
}

// Register all custom elements
if (!customElements.get('cart-item')) {
  customElements.define('cart-item', CartItem);
}

if (!customElements.get('quantity-controls')) {
  customElements.define('quantity-controls', QuantityControls);
}

if (!customElements.get('shipping-progress')) {
  customElements.define('shipping-progress', ShippingProgress);
}

if (!customElements.get('gift-progress')) {
  customElements.define('gift-progress', GiftProgress);
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

if (!customElements.get('choose-gift')) {
  customElements.define('choose-gift', ChooseGift);
}

if (!customElements.get('selling-plan-selector')) {
  customElements.define('selling-plan-selector', SellingPlanSelector);
}

if (!customElements.get('custom-cart')) {
  customElements.define('custom-cart', CustomCart);
}

if (!customElements.get('announcement-bar-carousel')) {
  customElements.define('announcement-bar-carousel', AnnouncementBarCarousel);
}

// Global cart functionality
window.customCart = {
  drawer: null,

  init() {
    this.drawer = document.getElementById('customCart');
    if (this.drawer) {
      this.bindCartTriggers();
    }
  },

  bindCartTriggers() {
    const cartButtons = document.querySelectorAll('[href="/cart"], [data-cart-open], .cart-icon');
    cartButtons.forEach(button => {
      button.addEventListener('click', this.handleCartButtonClick.bind(this));
    });
  },

  handleCartButtonClick(e) {
    e.preventDefault();
    this.open();
  },

  open() {
    if (this.drawer && typeof this.drawer.open === 'function') {
      this.drawer.open();
    }
  },

  close() {
    if (this.drawer && typeof this.drawer.close === 'function') {
      this.drawer.close();
    }
  },

  async addProduct(variantId, quantity = 1, properties = {}, sellingPlan) {
    if (!this.drawer) {
      throw new Error('Cart drawer not initialized');
    }
    
    try {
      const result = await this.drawer.addProduct(variantId, quantity, properties, sellingPlan);
      this.open();
      return result;
    } catch (error) {
      console.error('Failed to add product:', error);
      throw error;
    }
  },

  async refresh() {
    if (this.drawer && typeof this.drawer.refreshCart === 'function') {
      return await this.drawer.refreshCart();
    }
    return null;
  }
};

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.customCart.init();
});

// Handle add to cart forms
// NOTE: capture phase + propagation stops to prevent theme.js double-add
document.addEventListener('submit', async (e) => {
  const form = e.target;

  if (!form.matches('form[action*="/cart/add"]')) return;
  if (form.matches('.sp-offer-form, [data-sp-offer-checkout]')) return;

  // Stop the native submit AND any other JS submit handlers (theme.js, apps, etc.)
  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();

  try {
    const { variantId, quantity, properties, sellingPlan } = extractFormData(form);
    await window.customCart.addProduct(variantId, quantity, properties, sellingPlan);
  } catch (error) {
    console.error('Failed to add to cart:', error);
  }
}, true); // <-- `true` = capture phase


function extractFormData(form) {
  const formData = new FormData(form);
  const variantId = formData.get('id');
  const quantity = parseInt(formData.get('quantity')) || 1;
  let sellingPlan = formData.get('selling_plan');

  if (sellingPlan === '') {
    sellingPlan = null;
  }
  
  const properties = {};
  for (const [key, value] of formData.entries()) {
    if (key.startsWith('properties[')) {
      const propName = key.replace(/^properties\[|\]$/g, '');
      properties[propName] = value;
    }
  }
  
  return { variantId, quantity, properties, sellingPlan };
}